/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import { css } from 'styled-components';

import { theme } from '@toucan/config/theme';

import { InputProps, InputSize } from '@toucan/client/shared/components/input';

import { getPaddingLeft, getPaddingRight } from './style-helpers';

/**
 * Base styles for the Input component
 */
const baseStyles = ({
    onClear,
    prefixIcon,
    alwaysShowClearButton,
}: InputProps) =>
    css`
        // Input base styles
        width: 100%;
        position: relative;
        border: 0.1rem solid var(--colors-border-primary);
        border-radius: 0.4rem;
        -webkit-border-radius: 0.4rem;
        background: var(--colors-surface-primary);
        z-index: 2;
        ${theme.typography.body_normal}

        /* ios needs this prop to correctly set the input colour */
        -webkit-text-fill-color: var(--colors-text-secondary);
        color: var(--colors-text-secondary);
        line-height: 1;
        padding-top: var(--layout-spacing-12);
        padding-right: ${getPaddingRight(Boolean(onClear))};
        padding-bottom: var(--layout-spacing-12);
        padding-left: ${getPaddingLeft(Boolean(prefixIcon))};
        margin-top: var(--layout-spacing-24);

        // Label base styles
        & + label {
            ${theme.typography.body_small_highlight};
            color: var(--colors-text-secondary);
            display: block;
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            z-index: 1;
            padding-bottom: 0.8rem;

            // ellipses for long labels
            padding-right: 2rem;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }

        &[value=''] {
            border-color: var(--colors-border-primary);
            & + label {
                color: var(--colors-text-secondary);
            }
        }

        /* ios modifies opacity when input is disabled */
        &:disabled {
            opacity: 1;
        }

        &:hover {
            box-shadow: var(--effects-shadow-lg);
        }

        &:focus,
        &:active {
            box-shadow: none;
            outline: 0;
            border-color: var(--colors-text-secondary);
            box-shadow: inset 0 0 0 0.05rem var(--colors-surface-inverse);

            + label {
                color: var(--colors-text-secondary);
            }
        }

        &:invalid {
            box-shadow: none;
        }

        &::-ms-clear {
            display: none;
        }

        &::placeholder {
            /* ios needs this prop to correctly set the input colour */
            -webkit-text-fill-color: var(--colors-text-placeholder);
            color: var(--colors-text-placeholder);
        }

        // Number input in Chrome
        &[type='number']::-webkit-outer-spin-button,
        &[type='number']::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        // Number input in Firefox
        &[type='number'] {
            -moz-appearance: textfield;
        }

        &[type='search'] {
            // Removes default iOS border radius
            -webkit-appearance: none;

            // Search input in Chrome - Hides 'x'
            &::-webkit-search-decoration,
            &::-webkit-search-cancel-button,
            &::-webkit-search-results-button,
            &::-webkit-search-results-decoration {
                display: none;
            }
        }

        // Clear input button styles
        & ~ button {
            display: ${alwaysShowClearButton ? 'block' : 'none'};
            position: absolute;
            right: var(--layout-spacing-12);
            bottom: var(--layout-spacing-12);
            z-index: 4;
        }

        &:not([value='']) ~ button {
            display: flex;
            opacity: 1;
        }

        & ~ .prefix .icon {
            width: 2.4rem;
            height: 2.4rem;
            position: absolute;
            left: var(--layout-spacing-12);
            bottom: var(--layout-spacing-12);
            z-index: 2;
        }
    `;

/**
 * Styles for different sizes of input
 *
 * @param {InputProps} props             The input properties object
 * @param {InputSize}  props.inputSize   The input size
 * */
const sizeStyles = ({ inputSize, onClear, prefixIcon }: InputProps) => {
    switch (inputSize) {
        case InputSize.LARGE:
            return css`
                font-size: 2.4rem;
                padding-right: ${onClear ? '3.6rem' : '0'};
                padding-bottom: 1.6rem;
                padding-left: ${prefixIcon ? '4.8rem' : '0'};

                & + label {
                    font-size: 2.4rem;
                    padding-bottom: 1.6rem;
                    left: ${prefixIcon ? '4.8rem' : '0'};
                }

                & ~ button {
                    bottom: 1.2rem;
                }

                & ~ .prefix {
                    width: 3.6rem;
                    height: 3.6rem;
                    bottom: 1.2rem;
                }
            `;

        case InputSize.MEDIUM:
        default:
            return css``;
    }
};

/**
 * Styles for when the input is disabled.
 *
 * @param {InputProps}  props           The input properties object
 * @param {boolean}     props.disabled  True if the input is disabled
 * */
const disabledStyles = ({ disabled }: InputProps) => {
    if (!disabled) {
        return css``;
    }

    return css`
        background: var(--colors-surface-disabled);

        &,
        &:focus,
        &:active {
            /* ios needs this prop to correctly set the input colour */
            -webkit-text-fill-color: var(--colors-text-disabled);
            color: var(--colors-text-disabled);
            border-color: var(--colors-border-disabled);
        }

        &:hover {
            box-shadow: none;
        }

        &:hover + label,
        &:focus + label,
        & + label {
            font-weight: 400;
            color: var(--colors-text-disabled);
        }

        & ~ .prefix,
        & ~ .input-clear {
            border-color: var(--colors-border-disabled);
            > div {
                border-color: var(--colors-text-disabled);
            }
            svg {
                fill: var(--colors-text-disabled);
            }
        }
    `;
};

/**
 * Styles for when the input has an invalid value.
 *
 * @param {InputProps}  props           The input properties object
 * @param {string}      props.error     The error message
 * @param {boolean}     props.touched   True if the input has been touched
 * @param {boolean}     props.disabled  True if the input is disabled
 * */
const errorStyles = ({ forceError, error, touched, disabled }: InputProps) => {
    // if we dont have an error or touched and we dont have force error return
    // if this is disabled return regardless
    const showErrors = (error && touched) || forceError;
    if (!showErrors || disabled) {
        return css``;
    }

    return css`
        /* ios needs this prop to correctly set the input colour */
        -webkit-text-fill-color: var(--colors-state-error);
        color: var(--colors-state-error);

        & + label,
        &[value=''] + label,
        &:hover + label,
        &:focus + label,
        &:active + label {
            color: var(--colors-state-error);
        }

        &,
        &[value=''],
        &:hover,
        &:focus,
        &:active {
            border-color: var(--colors-state-error);
            box-shadow: inset 0 0 0 0.05rem var(--colors-state-error);
        }

        & ~ .prefix,
        & ~ .input-clear {
            border-color: var(--colors-state-error);
            > div {
                border-color: var(--colors-state-error);
            }
            svg {
                fill: var(--colors-state-error);
            }
        }
    `;
};

export const helpTextStyles = () => css`
    ${theme.typography.body_small}
`;

/**
 * The Input component styles
 *
 * @param {InputProps} props The input properties object
 */
export const styles = (props: InputProps) => css`
    ${baseStyles(props)}
    ${sizeStyles(props)}
    ${disabledStyles(props)}
    ${errorStyles(props)}
`;
